// The brief's type scale, carried into the components it shares with the standard report.
// Those components size text with `rt(px)` and weight non-heading emphasis with `RT_WEIGHT`:
// CSS variables the brief sets on its root (BRIEF_TYPE_VARS), with each component's own value
// as the fallback.

/**
 * The brief's three line spacings: long passages (Sayer's View, the open questions, the
 * sentences of open sections), short reading text (reasons, cards, the bottom line, one-liners),
 * and titles and the headline.
 */
export const LINE_HEIGHT = Object.freeze({ long: 1.6, short: 1.55, title: 1.3 });

/**
 * A line height the brief sets to one of its three steps; elsewhere the component keeps its
 * own (`fallback`). `lh.long(1.6)` → `var(--rt-lh-long, 1.6)`.
 */
export const lh = Object.freeze({
  long: (fallback) => `var(--rt-lh-long, ${fallback})`,
  short: (fallback) => `var(--rt-lh-short, ${fallback})`,
  title: (fallback) => `var(--rt-lh-title, ${fallback})`,
});

/** A font size the brief may remap: `var(--rt-12, 12px)`. */
export const rt = (px) => `var(--rt-${px}, ${px}px)`;

/**
 * A sentence in an open section (a chain's reasoning, a trigger's description, the outlook's
 * verdict): reading size inside the brief, the component's own size elsewhere.
 */
export const rtText = (px) => `var(--rt-text, ${px}px)`;

/** Emphasis on a value or label (never a heading): bold, but regular inside the brief. */
export const RT_WEIGHT = "var(--rt-weight, bold)";

/**
 * The brief's remaps, on its root: small supporting text (8–11px) to 12, supporting text
 * (12–13px) to 13, card titles (14px), larger reading text (16px) and the sentences of open
 * sections to 15, and value / label emphasis to medium weight. Headings keep their 600;
 * display numerals (23px and up) keep their size.
 */
export const BRIEF_TYPE_VARS = Object.freeze({
  "--rt-8": "12px",
  "--rt-9": "12px",
  "--rt-10": "12px",
  "--rt-11": "12px",
  "--rt-12": "13px",
  "--rt-13": "13px",
  "--rt-14": "15px",
  "--rt-16": "15px",
  "--rt-weight": "500",
  "--rt-text": "15px",
  "--rt-lh-long": String(LINE_HEIGHT.long),
  "--rt-lh-short": String(LINE_HEIGHT.short),
  "--rt-lh-title": String(LINE_HEIGHT.title),
});

/** How a report sets a link to evidence or a source: 12px, regular, light grey, underlined. */
export const REF_LINK_PX = 12;

/**
 * The style of a link to an evidence card or a source page in a report. `C.link` is the light
 * grey a link keeps even where a view lifts its grey text to white (the Mirabilis report).
 * @param {{link?: string, grayL: string}} C the report's colours
 */
export const refLinkStyle = (C) => ({
  color: C.link ?? C.grayL,
  fontSize: REF_LINK_PX,
  fontWeight: "normal",
  textDecoration: "underline",
  textDecorationColor: `${C.link ?? C.grayL}66`,
});
